// Слои над редактором (подсказка живой проверки, список «@») и элемент строки состояния «СТО: ошибки/замечания».
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { Ctx } from '../../core/registry';
import { useApp } from '../../core/store';
import { Icon } from '../../ui/icons';
import { overlay, useOverlay } from './overlay';
import { MentionPopup } from './mention';
import { ignores } from './profile';
import { rescan, applyFix, type InlineIssue } from './inlineLint';

const SEV_NAME = { E: 'Ошибка', W: 'Замечание', I: 'Совет' } as const;

function agoShort(t: number) {
  const s = Math.round((Date.now() - t) / 1000);
  return s < 60 ? '(только что)' : s < 3600 ? `(${Math.round(s / 60)} мин назад)` : s < 86400 ? `(${Math.round(s / 3600)} ч назад)` : `(${new Date(t).toLocaleDateString('ru-RU')})`;
}

function LintTip({ ctx }: { ctx: Ctx }) {
  const tip = useOverlay((s) => s.tip);
  useEffect(() => {
    if (!tip) return;
    let t: number | undefined;
    const move = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (el?.closest?.('.sto-lint, .lint-tip')) { window.clearTimeout(t); t = undefined; return; }
      if (t === undefined) t = window.setTimeout(() => overlay.getState().set({ tip: null }), 350);
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') overlay.getState().set({ tip: null }); };
    document.addEventListener('mousemove', move);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousemove', move); document.removeEventListener('keydown', key); window.clearTimeout(t); };
  }, [tip]);
  if (!tip || !ctx.editor) return null;
  const ed = ctx.editor;
  const apply = (x: InlineIssue, i: number) => {
    overlay.getState().set({ tip: null });
    try { if (!applyFix(ed, x, i)) ctx.toast('Уже исправлено', 'info'); } catch (e) { console.error(e); ctx.toast('Не удалось исправить: фрагмент уже изменён', 'warn'); }
    ed.commands.focus();
  };
  const ignore = (x: InlineIssue) => {
    const pid = ctx.store.getState().projectId;
    const s = ignores.list(pid); s.add(x.key); ignores.set(pid, s);
    overlay.getState().set({ tip: null });
    rescan(ed.view);
  };
  const style: React.CSSProperties = { left: Math.min(tip.x, window.innerWidth - 400), top: Math.min(tip.y, window.innerHeight - 160) };
  return createPortal(
    <div className="lint-tip" style={style} data-testid="lint-tip" onMouseDown={(e) => e.preventDefault()}>
      {tip.issues.map((x) => (
        <div key={x.key} className={`lint-tip-item sev-${x.sev}`}>
          <div className="lint-tip-head"><Icon name={x.sev === 'E' ? 'error' : x.sev === 'W' ? 'warn' : 'info'} size={14} />
            <b>{SEV_NAME[x.sev]}</b>{x.rule && <span className="muted">СТО п. {x.rule}</span>}</div>
          <div className="lint-tip-msg">{x.msg}</div>
          <div className="lint-tip-actions">
            {x.fixes.map((f, i) => <button key={i} type="button" className={'btn-mini' + (i === 0 ? ' btn-mini-primary' : '')} onClick={() => apply(x, i)}
              data-testid={i === 0 ? 'lint-fix' : undefined}>{f.label}</button>)}
            <button type="button" className="btn-mini" onClick={() => ignore(x)} title="Больше не показывать это замечание в этом отчёте">Пропустить</button>
          </div>
        </div>
      ))}
    </div>, document.body);
}

/** Строка состояния: счётчик замечаний живой проверки + ошибки последней полной проверки; щелчок - панель. */
export function StoStatus({ ctx }: { ctx: Ctx }) {
  const lint = useApp((s) => s.lint);
  const check = useApp((s) => s.check);
  const e = lint.filter((x) => x.sev === 'E').length;
  const w = lint.filter((x) => x.sev === 'W').length;
  const res = check.result;
  const open = () => {
    const p = ctx.store.getState().panels;
    ctx.store.getState().set({ panels: { ...p, right: 'check' } });
  };
  return (
    <>
      <button type="button" className="status-btn sto-status" onClick={open} data-testid="sto-status"
        title="Живая проверка по СТО (щелчок - панель «Проверка»)">
        <Icon name={e ? 'error' : w ? 'warn' : 'ok'} size={14} className={e ? 'c-err' : w ? 'c-warn' : 'c-ok'} />
        <span>СТО при наборе: {e || w ? `${e} ош. · ${w} зам.` : 'замечаний нет'}</span>
        {res && <span className={res.errors ? 'c-err' : 'c-ok'} title="Результат последней полной проверки (F7): сборка DOCX/PDF и ~340 правил">
          {` · полная проверка${check.at ? ' ' + agoShort(check.at) : ''}: ${res.errors ? res.errors + ' ош.' : 'OK'}`}</span>}
        {check.running && <><Icon name="spinner" size={13} className="spin" /> <span className="muted">{check.progress || 'Проверка…'}</span></>}
      </button>
      <LintTip ctx={ctx} />
      <MentionPopup />
    </>
  );
}
